import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookmarkCheck,
  Play,
  Search,
  Bookmark,
  Trash2
} from 'lucide-react';
import { getUserSavedQuestions, removeSavedQuestion } from '../../firebase/collections';
import { useRealtime } from '../../contexts/RealtimeContext';
import { QuestionComponent } from '../../components/QuestionTypes';

const SavedQuestions = () => {
  const navigate = useNavigate();
  const { currentUser } = useRealtime();
  const [savedQuestions, setSavedQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [showPracticeModal, setShowPracticeModal] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  // Get unique topics and difficulties from saved questions
  const topics = ['all', ...new Set(savedQuestions.map(q => q.topic).filter(Boolean))];
  const difficulties = ['all', ...new Set(savedQuestions.map(q => q.difficulty).filter(Boolean))];

  useEffect(() => {
    if (currentUser?.uid) {
      loadSavedQuestions();
    }
  }, [currentUser?.uid, loadSavedQuestions]);

  useEffect(() => {
    filterQuestions();
  }, [savedQuestions, searchTerm, selectedTopic, selectedDifficulty, filterQuestions]);

  const loadSavedQuestions = async () => {
    try {
      setLoading(true);
      const result = await getUserSavedQuestions(currentUser.uid);
      if (result.success) {
        setSavedQuestions(result.data);
      } else {
        // Failed to load saved questions
      }
    } catch (error) {
      // Error loading saved questions
    } finally {
      setLoading(false);
    }
  };

  const filterQuestions = () => {
    let filtered = savedQuestions;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(q => 
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.topic?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by topic
    if (selectedTopic !== 'all') {
      filtered = filtered.filter(q => q.topic === selectedTopic);
    }

    // Filter by difficulty
    if (selectedDifficulty !== 'all') {
      filtered = filtered.filter(q => q.difficulty === selectedDifficulty);
    }

    setFilteredQuestions(filtered);
  };

  const handleRemoveQuestion = async (questionId) => {
    try {
      const result = await removeSavedQuestion(questionId);
      if (result.success) {
        setSavedQuestions(prev => prev.filter(q => q.id !== questionId));
      } else {
      }
    } catch (error) {
      // Error removing question
    }
  };

  const handleStartPractice = () => {
    if (selectedQuestions.length === 0) {
      return;
    }

    // Create practice test data
    const practiceData = {
      id: `practice-${Date.now()}`,
      name: 'Practice with Saved Questions',
      questions: selectedQuestions.map(q => ({
        id: q.questionId,
        question: q.question,
        type: q.type,
        options: q.options,
        correctAnswer: q.correctAnswer,
        explanation: q.explanation,
        topic: q.topic,
        difficulty: q.difficulty,
        markingScheme: q.markingScheme
      })),
      duration: selectedQuestions.length * 2 * 60, // 2 minutes per question
      topic: 'Mixed Topics'
    };

    // Store in localStorage for the test interface to pick up
    localStorage.setItem(`practice-test-${practiceData.id}`, JSON.stringify(practiceData));
    
    // Navigate to test interface
    navigate(`/dashboard/test/${practiceData.id}`);
  };

  const toggleQuestionSelection = (questionId) => {
    setSelectedQuestions(prev => 
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const selectAllQuestions = () => {
    setSelectedQuestions(filteredQuestions.map(q => q.id));
  };

  const clearSelection = () => {
    setSelectedQuestions([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading saved questions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Saved Questions
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Your bookmarked questions for practice and revision
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{savedQuestions.length}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Saved</div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <select
                value={selectedTopic}
                onChange={(e) => setSelectedTopic(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {topics.map(topic => (
                  <option key={topic} value={topic}>
                    {topic === 'all' ? 'All Topics' : topic}
                  </option>
                ))}
              </select>

              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {difficulties.map(difficulty => (
                  <option key={difficulty} value={difficulty}>
                    {difficulty === 'all' ? 'All Difficulties' : difficulty}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={selectAllQuestions}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Select All
              </button>
              <button
                onClick={clearSelection}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Clear
              </button>
              <button
                onClick={() => setShowPracticeModal(true)}
                disabled={selectedQuestions.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play className="h-4 w-4 mr-2 inline" />
                Practice Selected ({selectedQuestions.length})
              </button>
            </div>
          </div>

          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search saved questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Questions List */}
        {filteredQuestions.length === 0 ? (
          <div className="text-center py-12">
            <Bookmark className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {savedQuestions.length === 0 ? 'No saved questions yet' : 'No questions match your filters'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {savedQuestions.length === 0 
                ? 'Start taking tests and save questions you want to practice later'
                : 'Try adjusting your search or filter criteria'
              }
            </p>
            {savedQuestions.length === 0 && (
              <button
                onClick={() => navigate('/dashboard/mock-tests')}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Take a Test
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredQuestions.map((savedQ, index) => (
              <motion.div
                key={savedQ.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedQuestions.includes(savedQ.id)}
                        onChange={() => toggleQuestionSelection(savedQ.id)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <div className="flex items-center space-x-2">
                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm">
                          {savedQ.topic || 'General'}
                        </span>
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full text-sm">
                          {savedQ.difficulty || 'Medium'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleRemoveQuestion(savedQ.id)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Remove from saved questions"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      {savedQ.question}
                    </h3>
                  </div>

                  {/* Question Options */}
                  <div className="mb-4">
                    <QuestionComponent
                      question={{
                        id: savedQ.questionId,
                        question: savedQ.question,
                        type: savedQ.type,
                        options: savedQ.options,
                        correctAnswer: savedQ.correctAnswer,
                        explanation: savedQ.explanation,
                        topic: savedQ.topic,
                        difficulty: savedQ.difficulty,
                        markingScheme: savedQ.markingScheme
                      }}
                      selectedAnswer={null}
                      onAnswerSelect={() => {}}
                      isReview={true}
                      correctAnswer={savedQ.correctAnswer}
                    />
                  </div>

                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center space-x-4">
                      <span>From: {savedQ.testName}</span>
                      <span>Saved: {new Date(savedQ.createdAt?.toDate?.() || savedQ.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Practice Modal */}
      {showPracticeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Start Practice Session
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You're about to start a practice session with {selectedQuestions.length} selected questions.
              This will create a timed test with these questions.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowPracticeModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPracticeModal(false);
                  handleStartPractice();
                }}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Start Practice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedQuestions;
